import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Options } from "../risks/iso-verklaringen";
import { useNavigate } from "react-router-dom";
import generatePDF from 'react-to-pdf';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import LoadPage from "../common/LoadPage";
import useAxiosFetch from "../common/useAxiosFetch";
import { updateDb } from "../services/updateDb";
import { useAppContext } from "../AppContext";
import { getCategoryValue } from "../common/helpers";

const ApplicabilityPage = () => {
  const { t, i18n } = useTranslation();
  const { currentOrg, openModal } = useAppContext();
  const { MODAL_TYPES } = require('../AppContext');
  const language = i18n.language;

  const { data: riskData, loading: risksLoading, error: risksError } = useAxiosFetch(`/api/risks/byOrg/${currentOrg.id}`);
  const { data: fetchedOrgData, loading: orgLoading, error: orgError } = useAxiosFetch(`/api/organizations/${currentOrg.id}`);
  const { data: featureLimits } = useAxiosFetch("/api/features/limits");

  const [filteredOptionsList, setFilteredOptionsList] = useState(Options(language, currentOrg.securityStandard));
  const [orgData, setOrgData] = useState({});
  const [filter, setFilter] = useState("AllCategories");
  const [editingIndex, setEditingIndex] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef();

  const navigate = useNavigate();

  useEffect(() => {
    setOrgData(fetchedOrgData);
    setFilteredOptionsList(Options(language, currentOrg.securityStandard));
  }, [fetchedOrgData, currentOrg, language]);

  const areAllMeasuresImplemented = (categoryValue) => {
    const measures = [];
    riskData.forEach(risk => {
      risk.measures?.forEach(measure => {
        if (measure.category && measure.status !== "dismissed") {
          measure.category.forEach(category => {
            if (getCategoryValue(category) === categoryValue) {
              measures.push(measure);
            }
          });
        }
      });
    });
    if (measures.length === 0) return false;
    return measures.every(measure => measure.status === "implemented");
  };

  const countCategoryOccurrences = (categoryValue) => {
    let count = 0;
    riskData.forEach(risk => {
      risk.measures?.forEach(measure => {
        if (measure.category && measure.status !== "dismissed") {
          measure.category.forEach(category => {
            if (getCategoryValue(category) === categoryValue) {
              count++;
            }
          });
        }
      });
    });
    return count;
  };

  const getRiskDescriptionsForCategory = (categoryValue) => {
    const descriptions = [];
    riskData.forEach(risk => {
      risk.measures?.forEach(measure => {
        if (measure.category && measure.status !== "dismissed") {
          measure.category.forEach(category => {
            if (getCategoryValue(category) === categoryValue) {
              descriptions.push({
                id: risk['id'],
                desc: risk.description,
                measure: measure.measure,
                measureStatus: measure.status
              });
            }
          });
        }
      });
    });

    if(descriptions.length > 6) {
      let showMore = {
        desc: descriptions.length - 6 + " " + t('more') + "..."
      };
      return descriptions.slice(0, 6).concat(showMore);
    }

    return descriptions;
  };
  
  const getNestedSoaData = (value) => {
    if (!orgData || !orgData.soa) return {};
    const [firstKey, ...rest] = value.split('.');
    const secondKey = rest.join('.');
    return orgData.soa[firstKey]?.[secondKey] || {};
  };

  const updateLocalState = (soaValue, newData) => {  
    const [primary, ...rest] = soaValue.split('.');
    const secondary = rest.join('.');
    setOrgData(prevState => {
      // Ensure soa exists
      let soa = prevState.soa || {};
      // Ensure primary key exists
      soa[primary] = soa[primary] || {};
      // Ensure secondary key exists
      soa[primary][secondary] = soa[primary][secondary] || {};
      return {
        ...prevState,
        soa: {
          ...soa,
          [primary]: {
            ...soa[primary],
            [secondary]: {
              ...soa[primary][secondary],
              ...newData
            }
          }
        }
      };
    });
  };

  const applyFilter = (currentFilter) => {
    let updatedOptions;
    const options = Options(language, currentOrg.securityStandard); 
    switch (currentFilter) {
      case "CategoriesWithoutRisks":
        updatedOptions = options.filter(item => countCategoryOccurrences(item.value) === 0);
        break;
      case "UnspecifiedCategories":
        updatedOptions = options.filter(item => {
          const currentSoa = getNestedSoaData(item.value);
          return (
            countCategoryOccurrences(item.value) === 0 &&
            !currentSoa.inclusionReason &&
            !currentSoa.exclusionJustification
          );
        });
        break;
      case "ImplementedCategories":
        updatedOptions = options.filter(item => {
          const currentSoa = getNestedSoaData(item.value);
          return currentSoa.implemented;
        });
        break;
      case "IncludedCategories":
        updatedOptions = options.filter(item => {
          const currentSoa = getNestedSoaData(item.value);
          const hasRisks = countCategoryOccurrences(item.value) > 0;
          const hasInclusionReasons = currentSoa.inclusionReason && currentSoa.inclusionReason.length > 0;
          return hasRisks || hasInclusionReasons;
        });
        break;
      case "ExcludedCategories":
        updatedOptions = options.filter(item => {
          const currentSoa = getNestedSoaData(item.value);
          return currentSoa.exclusionJustification;
        });
        break;
      case "NotImplemented":
        updatedOptions = options.filter(item => {
          const currentSoa = getNestedSoaData(item.value);
          return !currentSoa.implemented;
        });
        break;
      default:
        updatedOptions = [...options];
    }
    setFilteredOptionsList(updatedOptions);
  };

  const handleFilterChange = (e) => {
    const currentFilter = e.target.value;
    setFilter(currentFilter);
    applyFilter(currentFilter);
  };

  const getReasonLabel = (value) => {
    switch (value) {
      case "lo": return t('legal_obligation');
      case "co": return t('contractual_obligation');
      case "br": return t('business_requirement');
      case "ot": return t('other');
      default: return "";
    }
  };

  const handleImplementedChange = (soaValue, implemented) => {
    handleUpdate(soaValue, {implemented: implemented} );
  };

  const handleInclusionReasonChange = (soaValue, reasonValue) => {
    const currentReasons = getNestedSoaData(soaValue).inclusionReason || [];
    let updatedReasons;
  
    if (currentReasons.includes(reasonValue)) {
      updatedReasons = currentReasons.filter(r => r !== reasonValue);
    } else {
      updatedReasons = [...currentReasons, reasonValue];
    }
  
    handleUpdate(soaValue, { inclusionReason: updatedReasons });
  };

  let navigateRiskDetails = (riskId) => {
    if(riskId)
      navigate(`/viewrisk?riskId=${riskId}`);
  };

  const handleUpdate = async (soaId, data) => {
    await updateDb(`/api/organizations/soa/${currentOrg.id}`, {
      ...data,
      soaId
    });

    updateLocalState(soaId, data);
    if (filter === "UnspecifiedCategories") {
      applyFilter(filter);
    }
  };
 
  const handleExport = (exportType) => {
    // Check if environment is on trial plan (no paid subscription)
    const planId = featureLimits?.planInfo?.planId;
    const isTrialPlan = planId === 'trial';
    
    if (isTrialPlan) {
      openModal(MODAL_TYPES.UPGRADE, { featureType: 'Export' });
      setShowExportMenu(false);
      return;
    }

    // Proceed with export
    if (exportType === 'pdf') {
      exportTableToPDF();
    } else if (exportType === 'excel') {
      exportTableToExcel();
    }
    setShowExportMenu(false);
  };

  const exportTableToPDF = () => {
    setIsExporting(true);  // Turn on export mode

    setTimeout(() => {
      const options = {
        filename: 'export-statement-of-applicability.pdf',
        resolution: 1,
        page: {
           margin: 10,
           orientation: 'landscape',
        },
        overrides: {
           pdf: {
              compress: true
           }

        },
     };

      generatePDF(exportRef, options).then(() => {
        setIsExporting(false);  // Reset export mode after PDF is generated
      });
    }, 100);
  };
  
  const exportTableToExcel = async () => {
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('statement_of_applicability'), {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 7 }]
    });  

    // Merge and style the title, subtitle, and version cells as shown in the image
    worksheet.mergeCells('A1:B1');
    worksheet.getCell('A1').value = t('statement_of_applicability');
    worksheet.getCell('A1').font = { size: 15, bold: true };



    worksheet.mergeCells('A2:B2');
    worksheet.getCell('A2').value = currentOrg.name;

    worksheet.mergeCells('A3:B3');
    worksheet.getCell('A3').value = t('version') + ': 1.0';

    worksheet.mergeCells('A4:B4');
    worksheet.getCell('A4').value = t('date') + ': ' + new Date().toLocaleDateString();

    // Merge cells for the logo placeholder
    worksheet.mergeCells('C1:E4');
    // Assume logo is added here, with exceljs you'd typically use an image ID
    
    // Define the border style
    const borderStyles = {
      top: { style: 'thick', size: 1 },
      left: { style: 'thick', size: 1 },
      bottom: { style: 'thick', size: 1 },
      right: { style: 'thick', size: 1 }
    };

    // Apply the border styles to the range
    for(let row = 1; row <= 4; row++) {
      for(let col = 7; col <= 10; col++) { // Columns 'G' to 'J' are 7 to 10 in 1-indexed Excel
        const cell = worksheet.getCell(row, col);
        cell.border = borderStyles;
      }
    }

    // Merge cells for the header explanations and apply styles
    worksheet.mergeCells('G1:J4');
    const headerExplanationCell = worksheet.getCell('G1');

    // Set value as rich text to apply bold to specific parts
    headerExplanationCell.value = {
      richText: [
        { font: { bold: true }, text: t('ra') },
        { font: { bold: false }, text: " = " + t('risks_associated') + "\n" },
        { font: { bold: true }, text: t('lo') },
        { font: { bold: false }, text: " = " + t('legal_obligation') + "\n" },
        { font: { bold: true }, text: t('co') },
        { font: { bold: false }, text: " = " + t('contractual_obligation') + "\n" },
        { font: { bold: true }, text: t('br') },
        { font: { bold: false }, text: " = " + t('business_requirement') + "\n" },
      ]
    };
    headerExplanationCell.alignment = { wrapText: true, vertical: 'top' };


    // Set up the headers - assuming row 7 is the beginning of your table 
    const headerRow = worksheet.getRow(7);

    
    // Define columns and their headers with styling
    worksheet.columns = [
      { key: 'ID', width: 5 },
      { key: 'ControlName', width: 40 },
      { key: 'ControlText', width: 60 },
      { key: 'AssociatedRisks', width: 20 },
      { key: 'YN', width: 8 },
      { key: 'RA', width: 7 },
      { key: 'LO', width: 7 },
      { key: 'CO', width: 7 },
      { key: 'BR', width: 7 },
      { key: 'ReasonForExclusion', width: 30 },
      { key: 'ImplementationStatus', width: 25 },
    ];

    headerRow.getCell(1).value = 'ID';
    headerRow.getCell(2).value = t('control_name');
    headerRow.getCell(3).value = t('control_text');
    headerRow.getCell(4).value = t('associated_risks');
    headerRow.getCell(5).value = t('yn');
    headerRow.getCell(6).value = t('ra');
    headerRow.getCell(7).value = t('lo');
    headerRow.getCell(8).value = t('co');
    headerRow.getCell(9).value = t('br');
    headerRow.getCell(10).value = t('reason_for_exclusion');
    headerRow.getCell(11).value = t('implementation_status');

    // Apply bold style to headers
    headerRow.eachCell(cell => {
      cell.font = { bold: true };
    });

    // Define the range for the filter (covering all headers)
    const lastColumnLetter = worksheet.columns[worksheet.columns.length - 1].letter;
    const filterRange = `A7:${lastColumnLetter}7`;
    worksheet.autoFilter = filterRange;

    // Define styles for the "Implemented" status
      const implementedFill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFc6efce' } // Green background color
      };

      const implementedFont = {
        color: { argb: 'FF006101' }
      };

    // Populate the worksheet with data
    filteredOptionsList.forEach(item => {
      // Skip default custom control row when individual custom controls exist
      if (item.isCustomControl && countCategoryOccurrences(item.value) > 0) return;

      const riskDescriptions = getRiskDescriptionsForCategory(item.value).map(risk => risk.desc).join(", ");
      const currentSoa = getNestedSoaData(item.value);
  
      let lo = '', co = '', br = '', ot = '';
      if (currentSoa.inclusionReason) {
        lo = currentSoa.inclusionReason.includes('lo') ? '✔' : '';
        co = currentSoa.inclusionReason.includes('co') ? '✔' : '';
        br = currentSoa.inclusionReason.includes('br') ? '✔' : '';
        ot = currentSoa.inclusionReason.includes('ot') ? '✔' : '';
      }
  
      const exclusionJustification = currentSoa.exclusionJustification || "";
      const implementedStatus = currentSoa.implemented ? t('implemented') : t('not_implemented');

      const newRow = worksheet.addRow({
        ID: item.label.split(" ")[0],
        ControlName: item.label.substring(item.label.indexOf(" ") + 1),
        ControlText: item.controlTxt,
        AssociatedRisks: riskDescriptions,
        YN: (riskDescriptions || lo || co || br || ot) ? t('yes') : t('no'),
        RA: riskDescriptions ? "✔" : "",
        LO: lo,
        CO: co,
        BR: br,
        ReasonForExclusion: exclusionJustification,
        ImplementationStatus: implementedStatus,
      });

        // Apply styles if status is "Implemented"
        if (implementedStatus === t('implemented')) {
          const cell = newRow.getCell('ImplementationStatus');
          cell.fill = implementedFill;
          cell.font = implementedFont;
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        }
    });

    // Add custom control rows (matching the table's custom control section)
    const customControlOption = filteredOptionsList.find(item => item.isCustomControl);
    if (customControlOption && countCategoryOccurrences(customControlOption.value) > 0) {
      const customControls = [];
      riskData.forEach(risk => {
        risk.measures?.forEach(measure => {
          if (measure.category && measure.status !== "dismissed") {
            measure.category.forEach(category => {
              if (getCategoryValue(category) === customControlOption.value) {
                customControls.push({
                  desc: risk.description,
                  measure: measure.measure,
                  measureStatus: measure.status
                });
              }
            });
          }
        });
      });

      customControls.forEach((risk, index) => {
        const isImplemented = risk.measureStatus === 'implemented';
        const implementedStatus = isImplemented ? t('implemented') : t('not_implemented');
        const [prefix, baseNum] = customControlOption.value.split('.');
        const customId = prefix + '.' + (parseInt(baseNum) + index);

        const newRow = worksheet.addRow({
          ID: customId,
          ControlName: t('custom_control') + ' #' + (index + 1),
          ControlText: risk.measure,
          AssociatedRisks: risk.desc,
          YN: t('yes'),
          RA: '✔',
          LO: '',
          CO: '',
          BR: '',
          ReasonForExclusion: t('not_applicable'),
          ImplementationStatus: implementedStatus,
        });

        if (isImplemented) {
          const cell = newRow.getCell('ImplementationStatus');
          cell.fill = implementedFill;
          cell.font = implementedFont;
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        }
      });
    }
  
    // Write to a buffer
    const buffer = await workbook.xlsx.writeBuffer();
  
    // Use FileSaver to save the file
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, t('statement_of_applicability_xlsx'));
  };
  

  if (risksLoading || orgLoading)
    return <LoadPage />;

  if (risksError || orgError) 
    return <div>{t('error_fetching_data')}</div>;

  let customControlRisks = [];
  
  return (
    <div id="content">
      <select 
        className="blue-dropdown"
        value={filter}
        onChange={handleFilterChange}
      >
        <option value="AllCategories">{t('all_categories')}</option>
        <option value="CategoriesWithoutRisks">{t('categories_without_risks')}</option>
        <option value="UnspecifiedCategories">{t('unspecified_categories')}</option>
        <option value="IncludedCategories">{t('included_categories')}</option>
        <option value="ImplementedCategories">{t('implemented_categories')}</option>
        <option value="ExcludedCategories">{t('excluded_categories')}</option>
        <option value="NotImplemented">{t('not_implemented')}</option>
      </select>

      <div style={{ position: 'relative', display: 'inline-block', float: 'right' }}>
        <div className="white-border-btn" onClick={() => setShowExportMenu(!showExportMenu)}>
          <i className="icon-download" />&nbsp;&nbsp;Export
        </div>
        {showExportMenu && (
          <div className="dropdown-menu show" style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            borderRadius: 3,
            marginTop: 4,
            border: '2px solid var(--text-color)',
            zIndex: 1000,
            minWidth: 120
          }}>
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              onClick={() => handleExport('excel')}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--light-blue-bg-color)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <i className="icon-file-excel" style={{ color: 'var(--primary-color)' }} />
              Excel
            </div>
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
              onClick={() => handleExport('pdf')}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--light-blue-bg-color)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <i className="icon-file-pdf" style={{ color: 'var(--primary-color)' }} />
              PDF
            </div>
          </div>
        )}
      </div>

      <div className="clear-both" />

      <div className="content-bubble content-bubble-table">
        {isExporting && (<div className="pdf-date">{new Date().toLocaleDateString()}</div>)}
        <div className="table-wrapper">
          <table ref={exportRef}>
            <thead className="sticky-header">
              <tr>
                <td className="align-left" style={{width: '20%'}}>{t('category')}</td>
                <td className="align-left" style={{width: '25%'}}>{t('control_text')}</td>
                <td className="align-left">{t('associated_risks')}</td>
                <td className="align-left">{t('include_reason')}</td>
                <td className="align-left">{t('exclusion_justification')}</td>
                <td style={{width: language === 'nl' ? 190 : 160}}>{t('implemented_question')}</td>
              </tr>
            </thead>
            <tbody>
              {filteredOptionsList.map((item, itemIndex) => {
                if (!orgData) return null; // Skip rendering if orgData doesn't exist
                const riskCount = countCategoryOccurrences(item.value);
                const currentSoa = getNestedSoaData(item.value);

                if(item.isCustomControl && riskCount > 0) {
                    customControlRisks = getRiskDescriptionsForCategory(item.value);
                    return null; // Skip default row; individual custom controls rendered below
                }

                // currentSoa.inclusionReason will now be an array, so we initialize it with an empty array if it doesn't exist.
                const currentInclusionReasons = currentSoa.inclusionReason || [];

                return (
                  <tr key={itemIndex}>
                    <td className="align-left valign-top">{item.label}</td>
                    <td className="align-left limit-text-overflow valign-top" style={{fontSize: 13}}>{item.controlTxt}</td>
                    <td className="align-left limit-text-overflow valign-top" style={{fontSize: 13}}>
                      {getRiskDescriptionsForCategory(item.value).map((risk, index) => (
                        <div key={index} className="pointer-class" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => navigateRiskDetails(risk.id)}>
                          - {risk.desc}
                        </div>
                      ))}

                    </td>
                    <td className="align-left valign-top">
                    <div>
                        {riskCount > 0 ? (
                            <div className="selected-inclusion-reason">
                              {t('nr_risks_associated', { count: riskCount })}
                            </div>
                          ) : null
                        }
                        {["lo", "co", "br", "ot"].map(reasonValue => (
                            // Check if it's export mode and if the reason is not selected.
                            (!isExporting || (isExporting && currentInclusionReasons.includes(reasonValue))) ? (
                                <div 
                                    key={reasonValue}
                                    className={currentInclusionReasons.includes(reasonValue) ? "selected-inclusion-reason" : "deselected-inclusion-reason"}
                                    onClick={() => handleInclusionReasonChange(item.value, reasonValue)}
                                >
                                    {getReasonLabel(reasonValue)}
                                    <span className="hover-icon">
                                        {currentInclusionReasons.includes(reasonValue) ? 'x' : '+'}
                                    </span>
                                </div>
                            ) : null
                        ))}

                      </div>


                    </td>
                    <td className="align-left valign-top">
                      {editingIndex === itemIndex ? (
                        <textarea
                          rows="4"
                          style={{width: '100%'}}
                          name="exclusion_justification"
                          value={currentSoa.exclusionJustification || ''}
                          onChange={(e) => {
                            updateLocalState(item.value, { exclusionJustification: e.target.value });
                          }}
                          onBlur={(e) => {
                            handleUpdate(item.value, { exclusionJustification: e.target.value });
                            setEditingIndex(null); // Reset the editing index when the textarea loses focus
                          }}
                        />
                      ) : (
                        <div onClick={() => setEditingIndex(itemIndex)} className="pointer-class">
                          {currentSoa.exclusionJustification || (isExporting ? '' : t('click_to_add_justification'))}
                        </div>
                      )}
                    </td>
                    <td className="valign-top" style={{paddingTop: 20}}>
                        {/* 
                        <input 
                            type="checkbox" 
                            className="implemented-checkbox"
                            checked={currentSoa.implemented} 
                            onChange={() => handleImplementedChange(item.value, !currentSoa.implemented)} 
                        /> */}

                          <div className="toggle-pill-color">
                            <input type="checkbox" id={"implemented-" + itemIndex} name="check" 
                            checked={currentSoa && currentSoa.implemented ? true : false} 
                            onChange={() => handleImplementedChange(item.value, !currentSoa.implemented)} 
                            />
                            <label htmlFor={"implemented-" + itemIndex} />
                          </div>
                          {areAllMeasuresImplemented(item.value) && !currentSoa.implemented && (
                            <div
                              title={t('all_measures_implemented_tooltip')}
                              style={{
                                marginTop: 8,
                                fontSize: 11,
                                color: '#006101',
                                background: '#c6efce',
                                borderRadius: 4,
                                padding: '4px 7px',
                                cursor: 'pointer',
                                display: 'inline-block',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={() => handleImplementedChange(item.value, true)}
                            >
                              ✓ {t('mark_as_implemented')}
                            </div>
                          )}
                          {currentSoa.implemented && riskCount > 0 && !areAllMeasuresImplemented(item.value) && (
                            <div
                              title={t('not_all_measures_implemented_tooltip')}
                              style={{
                                marginTop: 8,
                                fontSize: 11,
                                color: '#9c0006',
                                background: '#ffc7ce',
                                borderRadius: 4,
                                padding: '4px 7px',
                                display: 'inline-block',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              ⚠ {t('not_all_measures_implemented')}
                            </div>
                          )}
                    </td>
                  </tr>
                );
              })}

              {/* for each risk defined with item.value == 9.1 add an additional row */}
              {customControlRisks.map((risk, index) => {
                const isImplemented = risk.measureStatus === 'implemented';
                const customControlItem = filteredOptionsList.find(i => i.isCustomControl);
                const [prefix, baseNum] = customControlItem ? customControlItem.value.split('.') : ['9', '1'];
                const customId = prefix + '.' + (parseInt(baseNum) + index);
                return (
                  <tr key={index}>
                    <td className="align-left valign-top">{customId} {t('custom_control')} #{(index+1)}</td>
                    <td className="align-left limit-text-overflow valign-top" style={{fontSize: 13}}>{risk.measure}</td>
                    <td className="align-left limit-text-overflow valign-top" style={{fontSize: 13}}>
                      <div className="pointer-class" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => navigateRiskDetails(risk.id)}>
                        - {risk.desc}
                      </div>
                    </td>
                    <td className="align-left valign-top">
                      <div className="selected-inclusion-reason">
                        {t('nr_risks_associated', { count: 1 })}
                      </div>
                    </td>
                    <td className="align-left valign-top">{t('not_applicable')}</td>
                    <td className="valign-top" style={{paddingTop: 20}}>
                      <div className="toggle-pill-color">
                        <input type="checkbox" id={"implemented-custom-" + index} name="check"
                          checked={isImplemented}
                          readOnly
                        />
                        <label htmlFor={"implemented-custom-" + index} style={{pointerEvents: 'none'}} />
                      </div>
                      <div style={{
                        marginTop: 8,
                        fontSize: 11,
                        color: isImplemented ? '#006101' : '#9c0006',
                        background: isImplemented ? '#c6efce' : '#ffc7ce',
                        borderRadius: 4,
                        padding: '4px 7px',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                      }}>
                        {t('measures_implemented_count', { implemented: isImplemented ? 1 : 0, total: 1 })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ApplicabilityPage;
